import React, {useMemo, useState} from 'react';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {
    AdditionalRowControllerProps,
    ControlledListProps,
    TAdditionalControlConfig,
    TAdditionalProps,
    TAdditionalRowControl
} from '@src/@types/components/ControlledList';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import Translate from '@src/components/Translate/Translate';
import classnames from '@src/helpers/classnames';
import AdditionalRowController from '@src/components/ControlledList/AdditionalRowController';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';

function VoidComponent() {
    return null;
}

function Label(props: {
    label: string | React.ReactNode,
    isLoading?: boolean,
}) {
    const {label, isLoading} = props;
    if (typeof label === 'string') {
        return <Translate message={isLoading ? '@common-phrases.loading' : label}/>;
    }

    if (isLoading) {
        return <LoadingIndicator type="border" size="sm"/>;
    }

    return <>{label}</>;
}

export const getPushNewItemAdditionalControlConfig = (
        {onClick, marginEnd = 1, size = 'sm', additionalClassName}: {
            onClick: () => void,
            marginEnd?: boolean | 0 | 1 | 2 | 3 | 4 | 5,
            size?: 'sm' | 'lg',
            additionalClassName?: string,
        },
        config?: Omit<TAdditionalControlConfig, 'onClick' | 'label'> & Partial<Pick<TAdditionalControlConfig, 'label'>>,
): TAdditionalControlConfig => ({
    className: classnames(`btn btn-outline-success`, additionalClassName, {
        [`btn-${size}`]: size,
        'me-1': marginEnd === true,
        [`me-${marginEnd}`]: typeof marginEnd === 'number',
    }),
    label: <i className="fa fa-plus"/>,
    ...(config ?? {}),
    onClick
});

function getResolvedParamsValues<
        T extends Record<string, any>,
        TKey extends keyof T
>(item: T, params: TKey[], args: any[]) {
    type TResolve<T> = T extends ((...args: any[]) => any) ? ReturnType<T> : T;

    type TRes = { [K in TKey]?: TResolve<T[K]> };

    return params.reduce<TRes>((res, key) => {
        res[key] = undefined;

        const value = item[key] ?? null;
        if (value == null) {
            return res;
        }

        if (typeof value === 'function' && !React.isValidElement(value)) {
            res[key] = value(...args);
        } else {
            res[key] = value;
        }

        return res;
    }, {});
}

function AdditionalControlNode<
        TOnClick extends TAdditionalControlConfig['onClick'],
>(props: {
    config: Omit<TAdditionalControlConfig, 'onClick' | 'disabled' | 'display' | 'label' | 'className'>
            & { onClick: TOnClick }
            & Pick<TAdditionalRowControl<any>, 'disabled' | 'display' | 'label' | 'className'>,
    onClickArguments?: Parameters<TOnClick>,
}) {
    const {config: item} = props;
    const [isLoading, setIsLoading] = useState(false);

    const Component = item.tag ?? 'button';

    const {disabled, display, label, className} = getResolvedParamsValues(
            item,
            ['disabled', 'display', 'label', 'className'],
            props.onClickArguments ?? [],
    );

    if (display === false) {
        return null;
    }

    return <Component
            type="button"
            className={classnames(className, 'me-1')}
            disabled={disabled || isLoading}
            onClick={() => {
                if (isLoading) {
                    return;
                }

                const result = (item.onClick as any)(...(props.onClickArguments ?? []));
                if (!(result instanceof Promise)) {
                    return;
                }

                setIsLoading(true);

                result.finally(() => {
                    setIsLoading(false);
                });
            }}
    >
        <Label label={label} isLoading={isLoading}/>
    </Component>;
}

function AdditionalControlNodes<
        TOnClick extends ((...args: any[]) => void | Promise<any>) = TAdditionalControlConfig['onClick']
>(props: {
    config?: Array<
            Omit<TAdditionalControlConfig, 'onClick' | 'disabled' | 'display' | 'label' | 'className'>
            & { onClick: TOnClick }
            & Pick<TAdditionalRowControl<any>, 'disabled' | 'display' | 'label' | 'className'>
    >,
    onClickArguments?: Parameters<TOnClick>,
}) {
    return <>{props.config?.map((item, index) => {
        return <AdditionalControlNode
                key={index}
                config={item}
                onClickArguments={props.onClickArguments}
        />;
    })}</>;
}

export default function useListContentPropsForAdditionalRow<
        Model extends IEmptyModel,
        AdditionalProps extends TAdditionalProps<Model> = Record<string, never>,
>(params: {
    additionalRow?: {
        mainRowComponent?: React.ComponentType<AdditionalRowControllerProps<Model, AdditionalProps>>
        additionalRowComponent?: AdditionalRowControllerProps<Model, AdditionalProps>['additionalRowComponent'],
    } & Omit<
            AdditionalRowControllerProps<Model, AdditionalProps>,
            'properties' | 'baseRenderer' | 'baseContentRenderer' | 'additionalRowComponent'
    >,
    additionalControls?: TAdditionalControlConfig[],
    removeRow?: {
        onClick?: (index: number, record: Model | undefined) => void,
        disabled?: boolean | ((record: Model) => boolean),
        display?: boolean | ((record: Model) => boolean),
    },
    additionalRowControls?: TAdditionalRowControl<Model>[],
    listContentProps?: Partial<ControlledListProps<Model>['listContentProps']>,
}): ControlledListProps<Model>['listContentProps'] {
    const {additionalRow, additionalControls, removeRow, listContentProps, additionalRowControls} = params;

    return useMemo(() => {
        return {
            tableHeadProps: {
                additionalControlNode: <AdditionalControlNodes config={additionalControls}/>,

                getTHProps(column) {
                    return additionalRow?.columnsTHProps?.[column.name] ?? {};
                },

                ...(listContentProps?.tableHeadProps ?? {}),
            },
            tableBodyProps: {
                renderControls(record, index, baseOnClickHandler, renderBaseControls) {
                    const onClick = removeRow?.onClick;
                    if (!onClick) {
                        return renderBaseControls();
                    }

                    const onClickHandler = () => onClick(index, record);

                    const additionalControls = record ? <AdditionalControlNodes<TAdditionalRowControl<Model>['onClick']>
                            config={additionalRowControls}
                            onClickArguments={[record, index]}
                    /> : null;

                    const {disabled, display} = getResolvedParamsValues(
                            removeRow,
                            ['disabled', 'display'],
                            [record],
                    );

                    if (display === false) {
                        return <>{additionalControls}</>;
                    }

                    if (record?.id) {
                        return <>
                            {additionalControls}

                            <ConfirmableButton
                                    size="sm"
                                    color="danger"
                                    disabled={disabled}
                                    onClick={onClickHandler}
                            >
                                <i className="fa fa-times"/>
                            </ConfirmableButton>
                        </>;
                    }

                    return <>
                        {additionalControls}

                        <button
                                type="button"
                                className="btn btn-sm btn-warning"
                                disabled={disabled}
                                onClick={onClickHandler}
                        >
                            <i className="fa fa-times"/>
                        </button>
                    </>;
                },

                getTDProps(column) {
                    return additionalRow?.columnsTDProps?.[column.name] ?? {};
                },

                renderRow(properties, baseRender, baseContentRenderer) {
                    const {
                        mainRowComponent: Component = AdditionalRowController,
                        additionalRowComponent = VoidComponent,
                        ...rest
                    } = additionalRow ?? {
                        addControlToColumnName: '~#$not-exists-column-name$#~'
                    } as NonNullable<typeof additionalRow>;

                    return <Component
                            properties={properties}
                            baseRenderer={baseRender}
                            baseContentRenderer={baseContentRenderer}
                            additionalRowComponent={additionalRowComponent}
                            {...rest}
                    />;
                },

                ...(listContentProps?.tableBodyProps ?? {}),
            },

            ...(listContentProps ?? {}),
        } as ControlledListProps<Model>['listContentProps'];
    }, [additionalControls, additionalRow, additionalRowControls, listContentProps, removeRow]);
}
